import { Component, OnInit, Input, ViewChild, TemplateRef } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { ThirdPartiesShared } from '@models/ThirdPartiesShared';
import { ThirdPartiesService } from '@services/third-parties.service';
import { ThirdPartiesAccount } from '@models/ThirdPartiesAccount';
import { TranslateService } from '@ngx-translate/core';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { ModalService } from '@services/support/modal.service';
import { LoaderService } from '@services/support/loader.service';
import { NgForm } from '@angular/forms';

@Component({
  selector: 'app-azure-details',
  templateUrl: './azure-details.component.html',
  styleUrls: ['./azure-details.component.scss']
})
export class AzureDetailsComponent implements OnInit {

  @ViewChild("addAzureTemplate", { static: true }) private addAzureTemplate: TemplateRef<any>;
  @ViewChild("chooseAzureTemplate", { static: true }) private chooseAzureTemplate: TemplateRef<any>;
  @ViewChild("deleteAzureTemplate", { static: true }) private deleteAzureTemplate: TemplateRef<any>;

  @Input() account: string;

  thirdPartiesSub: Subscription = null;

  thirdPartiesSharedAzureAccounts: any[] = null;
  thirdPartiesAccount: ThirdPartiesAccount = null;

  constructor(
    private thirdPartiesService: ThirdPartiesService,
    private translateService: TranslateService,
    private flashMessages: FlashMessagesService,
    private modalService: ModalService,
    private loaderService: LoaderService
  ) { }

  ngOnInit() {
    if (this.account) {
      this.thirdPartiesSub = this.thirdPartiesService.getThirdPartiesAccount(this.account).subscribe(account => {
        this.thirdPartiesAccount = account;
      });
    } else {
      this.thirdPartiesSub = this.thirdPartiesService.getThirdPartiesSharedAzureAccounts().subscribe(shared => {
        this.thirdPartiesSharedAzureAccounts = shared;
      });
    }
  }

  ngOnDestroy() {
    if (this.thirdPartiesSub) { this.thirdPartiesSub.unsubscribe() }
  }

  azureCredentialsFromShared(thirdPartiesShared: ThirdPartiesShared) {
    return thirdPartiesShared.azure ? Object.keys(thirdPartiesShared.azure).map(id => {
      thirdPartiesShared.azure[id].id = id;
      return thirdPartiesShared.azure[id];
    }) : [];
  }

  copied(event) {
    this.translateService.get('APP.MAIN.THIRD_PARTIES.AZURE_DETAILS.CREDENTIALS_COPIED').toPromise().then(result => {
      this.flashMessages.show(result, { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
    });
  }

  onAddAzureAccount() {
    const modalId = this.modalService.show({
      template: this.addAzureTemplate,
      context: {
        dataModel: {
          accountName: "",
          accountKey: ""
        },
        callbacks: {
          no: () => {
            this.modalService.hide(modalId);
          },
          add: (azureForm: NgForm) => {
            this.loaderService.show();
            azureForm.control.disable();

            this.thirdPartiesService.addAzureAccount(azureForm.value.accountName, azureForm.value.accountKey)
            .then(() => {
              this.flashMessages.show("Azure account added.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
              this.modalService.hide(modalId);
            })
            .catch(error => {
              this.flashMessages.show("Cannot add Azure account.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true });
              azureForm.control.enable();
            })
            .finally(() => {
              this.loaderService.hide();
            });
          }
        }
      }
    });
  }

  chooseAzureAccount(azure: any) {
    const modalId = this.modalService.show({
      template: this.chooseAzureTemplate,
      context: {
        dataModel: azure.credentials,
        callbacks: {
          no: () => {
            this.modalService.hide(modalId);
          },
          yes: () => {
            this.loaderService.show();
            this.thirdPartiesService.chooseTargetAzureAccount(azure.id)
            .then(() => {
              this.flashMessages.show("Azure account activated.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
            })
            .catch(error => {
              this.flashMessages.show("Cannot activate Azure account.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true });
            })
            .finally(() => {
              this.loaderService.hide();
              this.modalService.hide(modalId);
            });
          }
        }
      }
    });
  }

  deleteAzureAccount(azure: any) {
    const modalId = this.modalService.show({
      template: this.deleteAzureTemplate,
      context: {
        dataModel: azure.credentials,
        callbacks: {
          no: () => {
            this.modalService.hide(modalId);
          },
          yes: () => {
            this.loaderService.show();
            this.thirdPartiesService.deleteAzureAccount(azure.id)
            .then(() => {
              this.flashMessages.show("Azure account deleted.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
            })
            .catch(error => {
              this.flashMessages.show("Cannot delete Azure account.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
            })
            .finally(() => {
              this.loaderService.hide();
              this.modalService.hide(modalId);
            });
          }
        }
      }
    });
  }
}
